import * as RadixTabs from '@radix-ui/react-tabs';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface TabsProps {
  value: string;
  onValueChange: (v: string) => void;
  children: ReactNode;
  className?: string;
}

// Wrapper sobre Radix Tabs com estilo do design system (linha sublinhada no ativo).
export function Tabs({ value, onValueChange, children, className }: TabsProps) {
  return (
    <RadixTabs.Root
      value={value}
      onValueChange={onValueChange}
      className={cn('flex flex-col gap-3', className)}
    >
      {children}
    </RadixTabs.Root>
  );
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
}

export function TabsList({ children, className }: TabsListProps) {
  return (
    <RadixTabs.List
      className={cn(
        'flex items-center gap-1 border-b border-border',
        className,
      )}
    >
      {children}
    </RadixTabs.List>
  );
}

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  count?: number;
}

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(
  function TabsTrigger({ value, children, count }, ref) {
    return (
      <RadixTabs.Trigger
        ref={ref}
        value={value}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2',
          'border-transparent text-text-2 hover:text-text transition-colors',
          'data-[state=active]:border-accent data-[state=active]:text-text',
          '-mb-px',
        )}
      >
        {children}
        {count != null && (
          <span className="text-[10px] tabular-nums text-text-muted">{count}</span>
        )}
      </RadixTabs.Trigger>
    );
  },
);

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  return (
    <RadixTabs.Content
      value={value}
      className={cn('outline-none focus-visible:ring-2 focus-visible:ring-accent', className)}
    >
      {children}
    </RadixTabs.Content>
  );
}
